import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  CalendarDays, ChevronLeft, ChevronRight, Plus, MapPin, Users, Trash2, X,
  DollarSign, ArrowLeftRight, ShoppingCart, Receipt, Loader2, Pencil, BarChart3, Download, FileText, TrendingUp,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const fmt = (n) => { const v = Number(n || 0); return `${v < 0 ? "-" : ""}$${Math.abs(v).toFixed(2)}`; };
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const FEE_FIELDS = [
  { key: "table_fee", label: "Table fee" },
  { key: "hotel_fee", label: "Hotel" },
  { key: "food_gas_fee", label: "Food / Gas" },
  { key: "travel_fee", label: "Travel" },
  { key: "other_fee", label: "Other" },
];

// local <datetime-local string> <-> ISO (UTC) helpers
const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fromLocalInput = (s) => (s ? new Date(s).toISOString() : "");
const dateKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const STATUS_STYLE = {
  upcoming: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  active: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  past: "bg-slate-500/15 text-slate-400 border-slate-500/30",
};

const emptyForm = () => ({
  name: "", location: "", start: "", end: "",
  table_fee: "", hotel_fee: "", food_gas_fee: "", travel_fee: "", other_fee: "",
  staff: [], notes: "",
});

export default function Shows() {
  const [shows, setShows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [team, setTeam] = useState([]);
  const [cursor, setCursor] = useState(() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1); });
  const [modal, setModal] = useState(null); // { form, id }
  const [statsFor, setStatsFor] = useState(null); // show
  const [stats, setStats] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/shows");
      setShows(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    api.get("/team").then(({ data }) => setTeam(data)).catch(() => {});
  }, []);

  const byDay = useMemo(() => {
    const m = {};
    for (const s of shows) {
      if (!s.start) continue;
      const start = new Date(s.start);
      const end = s.end ? new Date(s.end) : start;
      let d = new Date(start.getFullYear(), start.getMonth(), start.getDate());
      const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());
      let guard = 0;
      while (d <= last && guard < 366) {
        const k = dateKey(d);
        (m[k] = m[k] || []).push(s);
        d = new Date(d); d.setDate(d.getDate() + 1); guard++;
      }
    }
    return m;
  }, [shows]);

  // Upcoming & active first (soonest first), then past (most recent first).
  const orderedShows = useMemo(() => {
    const live = shows.filter((s) => s.status !== "past").sort((a, b) => (a.start || "").localeCompare(b.start || ""));
    const past = shows.filter((s) => s.status === "past").sort((a, b) => (b.start || "").localeCompare(a.start || ""));
    return [...live, ...past];
  }, [shows]);

  const monthCells = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const start = new Date(first);
    start.setDate(first.getDate() - first.getDay());
    return Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  }, [cursor]);

  const openCreate = (day) => {
    const f = emptyForm();
    if (day) {
      const s = new Date(day); s.setHours(9, 0, 0, 0);
      const e = new Date(day); e.setHours(17, 0, 0, 0);
      f.start = toLocalInput(s.toISOString());
      f.end = toLocalInput(e.toISOString());
    }
    setModal({ form: f, id: null });
  };

  const openEdit = (s) => {
    setModal({
      id: s.id,
      form: {
        name: s.name || "", location: s.location || "",
        start: toLocalInput(s.start), end: toLocalInput(s.end),
        table_fee: s.table_fee || "", hotel_fee: s.hotel_fee || "", food_gas_fee: s.food_gas_fee || "",
        travel_fee: s.travel_fee || "", other_fee: s.other_fee || "",
        staff: (s.staff || []).map((x) => ({ ...x })), notes: s.notes || "",
      },
    });
  };

  const setF = (patch) => setModal((m) => ({ ...m, form: { ...m.form, ...patch } }));

  const save = async () => {
    const f = modal.form;
    if (!f.name.trim()) { toast.error("Give the show a name"); return; }
    if (!f.start || !f.end) { toast.error("Set a start and end date/time"); return; }
    if (fromLocalInput(f.end) < fromLocalInput(f.start)) { toast.error("End must be after start"); return; }
    setSaving(true);
    const body = {
      name: f.name.trim(), location: f.location.trim(),
      start: fromLocalInput(f.start), end: fromLocalInput(f.end),
      table_fee: Number(f.table_fee) || 0, hotel_fee: Number(f.hotel_fee) || 0,
      food_gas_fee: Number(f.food_gas_fee) || 0, travel_fee: Number(f.travel_fee) || 0,
      other_fee: Number(f.other_fee) || 0, notes: f.notes.trim(),
      staff: (f.staff || []).filter((s) => (s.name || "").trim()).map((s) => ({ name: s.name.trim(), user_id: s.user_id || "", wage: Number(s.wage) || 0 })),
    };
    try {
      if (modal.id) await api.put(`/shows/${modal.id}`, body);
      else await api.post("/shows", body);
      toast.success(modal.id ? "Show updated" : "Show added");
      setModal(null);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this show?")) return;
    try { await api.delete(`/shows/${id}`); toast.success("Show deleted"); setModal(null); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openStats = async (s) => {
    setStatsFor(s); setStats(null);
    try { const { data } = await api.get(`/shows/${s.id}/stats`); setStats(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setStatsFor(null); }
  };

  const addStaff = () => setF({ staff: [...(modal.form.staff || []), { name: "", user_id: "", wage: "" }] });
  const setStaff = (i, patch) => setF({ staff: modal.form.staff.map((s, idx) => (idx === i ? { ...s, ...patch } : s)) });
  const rmStaff = (i) => setF({ staff: modal.form.staff.filter((_, idx) => idx !== i) });

  const fmtRange = (s) => {
    if (!s.start) return "";
    const a = new Date(s.start), b = new Date(s.end);
    const d = (x) => x.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    const t = (x) => x.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
    const sameDay = a.toDateString() === b.toDateString();
    return sameDay ? `${d(a)} · ${t(a)}–${t(b)}` : `${d(a)} ${t(a)} – ${d(b)} ${t(b)}`;
  };

  const today = new Date();

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-100 flex items-center gap-2">
            <CalendarDays className="h-7 w-7 text-amber-400" /> Shows
          </h1>
          <p className="text-sm text-slate-400 mt-1">Plan events, assign staff & costs, and track what you sold, traded and bought at each show.</p>
        </div>
        <button onClick={() => openCreate(null)} data-testid="show-add-button"
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-amber-500 text-[#1a1205] font-semibold text-sm hover:bg-amber-400">
          <Plus className="h-4 w-4" /> Add show
        </button>
      </div>

      {/* Calendar */}
      <div className="rounded-2xl border p-4 mb-8" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }}>
        <div className="flex items-center justify-between mb-3">
          <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} data-testid="cal-prev"
            className="h-9 w-9 flex items-center justify-center rounded-lg border text-slate-300 hover:bg-[var(--pk-hover)]" style={{ borderColor: "var(--pk-border)" }}><ChevronLeft className="h-4 w-4" /></button>
          <div className="font-heading font-bold text-slate-100">{MONTHS[cursor.getMonth()]} {cursor.getFullYear()}</div>
          <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} data-testid="cal-next"
            className="h-9 w-9 flex items-center justify-center rounded-lg border text-slate-300 hover:bg-[var(--pk-hover)]" style={{ borderColor: "var(--pk-border)" }}><ChevronRight className="h-4 w-4" /></button>
        </div>
        <div className="grid grid-cols-7 gap-1 mb-1">
          {DOW.map((d) => <div key={d} className="text-center text-[11px] font-semibold text-slate-500 py-1">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {monthCells.map((d, i) => {
            const inMonth = d.getMonth() === cursor.getMonth();
            const isToday = d.toDateString() === today.toDateString();
            const items = byDay[dateKey(d)] || [];
            return (
              <button key={i} onClick={() => openCreate(d)} data-testid={`cal-day-${dateKey(d)}`}
                className={`min-h-[76px] rounded-lg border p-1.5 text-left transition-colors hover:border-amber-500/50 ${inMonth ? "" : "opacity-40"}`}
                style={{ background: isToday ? "var(--pk-hover)" : "transparent", borderColor: isToday ? "rgb(245 158 11 / 0.4)" : "var(--pk-border-subtle)" }}>
                <div className={`text-[11px] mb-1 ${isToday ? "text-amber-400 font-bold" : "text-slate-500"}`}>{d.getDate()}</div>
                <div className="space-y-0.5">
                  {items.slice(0, 3).map((s) => (
                    <div key={s.id} onClick={(e) => { e.stopPropagation(); openStats(s); }}
                      className="truncate text-[10px] px-1 py-0.5 rounded bg-amber-500/20 text-amber-200 hover:bg-amber-500/30" title={s.name}>
                      {s.name}
                    </div>
                  ))}
                  {items.length > 3 && <div className="text-[10px] text-slate-500 px-1">+{items.length - 3} more</div>}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* List (soonest -> furthest) */}
      <h2 className="font-heading text-lg font-bold text-slate-100 mb-3">All shows</h2>
      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>
      ) : shows.length === 0 ? (
        <div className="rounded-2xl border py-14 text-center text-slate-500" style={{ borderColor: "var(--pk-border)" }} data-testid="shows-empty">
          <CalendarDays className="h-8 w-8 mx-auto mb-3 text-slate-700" /> No shows yet — add your first event above.
        </div>
      ) : (
        <div className="space-y-3" data-testid="shows-list">
          {orderedShows.map((s) => (
            <div key={s.id} className="rounded-2xl border p-4 flex items-center gap-4 flex-wrap" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }} data-testid="show-row">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-heading font-bold text-slate-100">{s.name}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border capitalize ${STATUS_STYLE[s.status] || ""}`}>{s.status}</span>
                </div>
                <div className="text-xs text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
                  <span className="flex items-center gap-1"><CalendarDays className="h-3 w-3" /> {fmtRange(s)}</span>
                  {s.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {s.location}</span>}
                  {(s.staff?.length || 0) > 0 && <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {s.staff.length} staff</span>}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[11px] text-slate-500">Event profit</div>
                <div className={`font-mono-num font-semibold ${Number(s.net || 0) >= 0 ? "text-emerald-400" : "text-rose-400"}`} data-testid="show-net-profit">{fmt(s.net)}</div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => openStats(s)} data-testid="show-stats-button" className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-500/15 text-amber-300 text-xs font-semibold hover:bg-amber-500/25"><BarChart3 className="h-3.5 w-3.5" /> Stats</button>
                <button onClick={() => openEdit(s)} data-testid="show-edit-button" className="h-9 w-9 flex items-center justify-center rounded-lg border text-slate-400 hover:bg-[var(--pk-hover)]" style={{ borderColor: "var(--pk-border)" }}><Pencil className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <ShowModal form={modal.form} id={modal.id} team={team} saving={saving}
          onClose={() => setModal(null)} onSave={save} onDelete={remove} setF={setF}
          addStaff={addStaff} setStaff={setStaff} rmStaff={rmStaff} />
      )}
      {statsFor && (
        <StatsDrawer show={statsFor} stats={stats} onClose={() => { setStatsFor(null); setStats(null); }} fmtRange={fmtRange} />
      )}
    </div>
  );
}

function ShowModal({ form, id, team, saving, onClose, onSave, onDelete, setF, addStaff, setStaff, rmStaff }) {
  const inputCls = "w-full px-3 py-2 rounded-xl border bg-transparent text-sm text-slate-100 outline-none focus:ring-2 focus:ring-amber-500/40";
  const bd = { borderColor: "var(--pk-border)" };
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto bg-black/60" onClick={onClose}>
      <div className="w-full max-w-lg my-6 rounded-2xl border animate-fade-up" style={{ background: "#0D1017", borderColor: "var(--pk-border)" }} onClick={(e) => e.stopPropagation()} data-testid="show-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b" style={bd}>
          <h2 className="font-heading font-bold text-slate-100">{id ? "Edit show" : "Add show"}</h2>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-200" data-testid="show-modal-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          <div>
            <label className="text-xs text-slate-400">Show name</label>
            <input value={form.name} onChange={(e) => setF({ name: e.target.value })} className={inputCls} style={bd} placeholder="Regional Card Expo" data-testid="show-name-input" />
          </div>
          <div>
            <label className="text-xs text-slate-400">Venue / location</label>
            <input value={form.location} onChange={(e) => setF({ location: e.target.value })} className={inputCls} style={bd} placeholder="Convention Center, Hall B" data-testid="show-location-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400">Starts</label>
              <input type="datetime-local" value={form.start} onChange={(e) => setF({ start: e.target.value })} className={inputCls} style={bd} data-testid="show-start-input" />
            </div>
            <div>
              <label className="text-xs text-slate-400">Ends</label>
              <input type="datetime-local" value={form.end} onChange={(e) => setF({ end: e.target.value })} className={inputCls} style={bd} data-testid="show-end-input" />
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold text-slate-300 mb-2">Expenses</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {FEE_FIELDS.map((f) => (
                <div key={f.key}>
                  <label className="text-[11px] text-slate-500">{f.label}</label>
                  <div className="flex items-center rounded-xl border px-2" style={bd}>
                    <span className="text-slate-500 text-sm">$</span>
                    <input type="number" inputMode="decimal" value={form[f.key]} onChange={(e) => setF({ [f.key]: e.target.value })}
                      className="w-full bg-transparent outline-none text-sm text-slate-100 py-2 px-1 font-mono-num" placeholder="0" data-testid={`show-fee-${f.key}`} />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-semibold text-slate-300">Staff working this show</div>
              <button onClick={addStaff} className="text-xs text-amber-400 hover:text-amber-300 flex items-center gap-1" data-testid="show-add-staff"><Plus className="h-3 w-3" /> Add</button>
            </div>
            <div className="space-y-2">
              {(form.staff || []).map((s, i) => (
                <div key={i} className="flex items-center gap-2" data-testid="show-staff-row">
                  <input list="team-names" value={s.name} onChange={(e) => {
                    const match = team.find((t) => (t.name || t.email) === e.target.value);
                    setStaff(i, { name: e.target.value, user_id: match?.id || "" });
                  }} className="flex-1 px-3 py-2 rounded-xl border bg-transparent text-sm text-slate-100 outline-none" style={bd} placeholder="Name" data-testid="show-staff-name" />
                  <div className="flex items-center rounded-xl border px-2 w-28" style={bd}>
                    <span className="text-slate-500 text-sm">$</span>
                    <input type="number" inputMode="decimal" value={s.wage} onChange={(e) => setStaff(i, { wage: e.target.value })}
                      className="w-full bg-transparent outline-none text-sm text-slate-100 py-2 px-1 font-mono-num" placeholder="wage" data-testid="show-staff-wage" />
                  </div>
                  <button onClick={() => rmStaff(i)} data-testid="show-staff-remove" className="text-slate-500 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
              {(form.staff || []).length === 0 && <p className="text-[11px] text-slate-600">No staff assigned.</p>}
            </div>
            <datalist id="team-names">
              {team.map((t) => <option key={t.id} value={t.name || t.email} />)}
            </datalist>
          </div>
          <div>
            <label className="text-xs text-slate-400">Notes</label>
            <textarea value={form.notes} onChange={(e) => setF({ notes: e.target.value })} rows={2} className={inputCls} style={bd} placeholder="Booth #, setup time, anything to remember…" data-testid="show-notes-input" />
          </div>
        </div>
        <div className="flex items-center justify-between gap-2 px-5 py-4 border-t" style={bd}>
          {id ? <button onClick={() => onDelete(id)} className="text-rose-400 hover:text-rose-300 text-sm flex items-center gap-1" data-testid="show-delete-button"><Trash2 className="h-4 w-4" /> Delete</button> : <span />}
          <div className="flex gap-2">
            <button onClick={onClose} data-testid="show-cancel-button" className="px-4 py-2 rounded-xl border text-sm text-slate-300 hover:bg-[var(--pk-hover)]" style={bd}>Cancel</button>
            <button onClick={onSave} disabled={saving} className="px-5 py-2 rounded-xl bg-amber-500 text-[#1a1205] font-semibold text-sm hover:bg-amber-400 disabled:opacity-60 flex items-center gap-2" data-testid="show-save-button">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} {id ? "Save changes" : "Add show"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatRow({ icon: Icon, color, label, value, sub }) {
  return (
    <div className="rounded-xl border p-4" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}>
      <div className="flex items-center gap-2 text-xs text-slate-400 mb-1"><Icon className={`h-4 w-4 ${color}`} /> {label}</div>
      <div className="text-2xl font-bold font-mono-num text-slate-100">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function StatsDrawer({ show, stats, onClose, fmtRange }) {
  const money = (n) => { const v = Number(n || 0); return `${v < 0 ? "-" : ""}$${Math.abs(v).toFixed(2)}`; };
  const safeName = (show.name || "show").replace(/[^a-z0-9]+/gi, "_");

  const exportCSV = () => {
    const rows = [
      ["Show", show.name], ["Location", show.location || ""], ["Window", fmtRange(show)], [],
      ["Section", "Metric", "Value"],
      ["Sales", "Revenue", stats.sales.revenue], ["Sales", "Cost (COGS)", stats.sales.cost], ["Sales", "Profit", stats.sales.profit],
      ["Sales", "Orders", stats.sales.orders], ["Sales", "Units", stats.sales.units], ["Sales", "List value", stats.sales.list_value],
      ["Trades", "Count", stats.trades.count], ["Trades", "Value out", stats.trades.value_out],
      ["Trades", "Credit in", stats.trades.credit_in], ["Trades", "Profit", stats.trades.profit], ["Trades", "Cash collected", stats.trades.cash_collected],
      ["Trades", "Cards out", stats.trades.cards_out], ["Trades", "Cards in", stats.trades.cards_in],
      ["Purchases", "Spend", stats.purchases.spend], ["Purchases", "Cards", stats.purchases.cards],
      ["Expenses", "Fees", stats.expenses.fees], ["Expenses", "Wages", stats.expenses.wages], ["Expenses", "Total", stats.expenses.total],
      ["Net", "On-site profit", stats.net],
    ];
    if (stats.by_staff?.length) {
      rows.push([]); rows.push(["Staff", "Sales", "Units", "Trades", "Trade value"]);
      stats.by_staff.forEach((s) => rows.push([s.name, s.sales, s.units, s.trades, s.trade_value]));
    }
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = `${safeName}_PnL.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV downloaded");
  };

  const exportPDF = () => {
    const w = window.open("", "_blank", "width=800,height=900");
    if (!w) { toast.error("Allow pop-ups to print/save as PDF"); return; }
    const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
    const row = (l, v) => `<tr><td>${esc(l)}</td><td style="text-align:right">${esc(v)}</td></tr>`;
    const staff = (stats.by_staff || []).map((s) =>
      `<tr><td>${esc(s.name)}</td><td style="text-align:right">${money(s.sales)}</td><td style="text-align:right">${s.units}</td><td style="text-align:right">${s.trades}</td><td style="text-align:right">${money(s.trade_value)}</td></tr>`).join("");
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${esc(show.name)} — P&L</title>
      <style>body{font-family:Arial,Helvetica,sans-serif;color:#111;padding:32px;max-width:720px;margin:auto}
      h1{margin:0 0 4px;font-size:22px}.sub{color:#666;margin:0 0 20px;font-size:13px}
      h2{font-size:14px;margin:22px 0 6px;border-bottom:2px solid #eee;padding-bottom:4px}
      table{width:100%;border-collapse:collapse;font-size:13px}td,th{padding:6px 8px;border-bottom:1px solid #eee}
      th{text-align:left;background:#f6f6f6}.net{font-size:20px;font-weight:bold;color:${stats.net >= 0 ? "#047857" : "#be123c"}}</style></head>
      <body><h1>${esc(show.name)}</h1><p class="sub">${esc(show.location || "")} · ${esc(fmtRange(show))}</p>
      <h2>Summary</h2><table>
      ${row("Sales revenue", money(stats.sales.revenue))}${row("Sales cost (COGS)", money(stats.sales.cost))}${row("Sale profit", money(stats.sales.profit))}${row("Sales orders / units", `${stats.sales.orders} / ${stats.sales.units}`)}
      ${row("Trade value out", money(stats.trades.value_out))}${row("Trade profit", money(stats.trades.profit))}${row("Trade cash collected", money(stats.trades.cash_collected))}${row("Trade credit given", money(stats.trades.credit_in))}
      ${row("Purchases spend (not in net)", money(stats.purchases.spend))}${row("Event costs (fees + wages)", money(stats.expenses.total))}
      </table><h2>Net on-site profit</h2><p class="net">${money(stats.net)}</p>
      ${staff ? `<h2>By staff</h2><table><tr><th>Name</th><th style="text-align:right">Sales</th><th style="text-align:right">Units</th><th style="text-align:right">Trades</th><th style="text-align:right">Trade value</th></tr>${staff}</table>` : ""}
      </body></html>`);
    w.document.close(); w.focus(); setTimeout(() => w.print(), 300);
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60" onClick={onClose}>
      <div className="w-full max-w-md h-full overflow-y-auto border-l animate-fade-up" style={{ background: "#0D1017", borderColor: "var(--pk-border)", animationDuration: "0.2s" }} onClick={(e) => e.stopPropagation()} data-testid="show-stats-drawer">
        <div className="flex items-center justify-between px-5 py-4 border-b sticky top-0" style={{ background: "#0D1017", borderColor: "var(--pk-border)" }}>
          <div>
            <h2 className="font-heading font-bold text-slate-100">{show.name}</h2>
            <p className="text-[11px] text-slate-500">{fmtRange(show)}</p>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-200" data-testid="show-stats-close"><X className="h-5 w-5" /></button>
        </div>
        {!stats ? (
          <div className="py-20 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>
        ) : (
          <div className="p-5 space-y-4">
            <div className={`rounded-xl border p-4 ${stats.net >= 0 ? "border-emerald-500/30 bg-emerald-500/10" : "border-rose-500/30 bg-rose-500/10"}`} data-testid="show-net">
              <div className="text-xs text-slate-400 mb-1">Net on-site profit</div>
              <div className={`text-3xl font-bold font-mono-num ${stats.net >= 0 ? "text-emerald-300" : "text-rose-300"}`}>{money(stats.net)}</div>
              <div className="text-[11px] text-slate-500 mt-1">Sale profit + trade profit − event costs</div>
            </div>
            <div className="flex gap-2">
              <button onClick={exportCSV} data-testid="show-export-csv" className="flex-1 flex items-center justify-center gap-2 py-2 rounded-xl border text-xs font-semibold text-slate-200 hover:bg-[var(--pk-hover)]" style={{ borderColor: "var(--pk-border)" }}><Download className="h-3.5 w-3.5" /> Export CSV</button>
              <button onClick={exportPDF} data-testid="show-export-pdf" className="flex-1 flex items-center justify-center gap-2 py-2 rounded-xl border text-xs font-semibold text-slate-200 hover:bg-[var(--pk-hover)]" style={{ borderColor: "var(--pk-border)" }}><FileText className="h-3.5 w-3.5" /> Print / PDF</button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <StatRow icon={TrendingUp} color="text-emerald-400" label="Sale profit" value={money(stats.sales.profit)} sub={`${money(stats.sales.revenue)} sales · ${stats.sales.units} cards`} />
              <StatRow icon={ArrowLeftRight} color="text-cyan-400" label="Trade profit" value={money(stats.trades.profit)} sub={`${stats.trades.count} trades · ${money(stats.trades.cash_collected)} cash`} />
              <StatRow icon={ShoppingCart} color="text-violet-400" label="Purchases" value={money(stats.purchases.spend)} sub={`${stats.purchases.cards} cards in`} />
              <StatRow icon={Receipt} color="text-rose-400" label="Event costs" value={money(stats.expenses.total)} sub={`fees ${money(stats.expenses.fees)} · wages ${money(stats.expenses.wages)}`} />
            </div>
            {(stats.by_staff?.length || 0) > 0 && (
              <div data-testid="show-by-staff">
                <div className="text-xs font-semibold text-slate-300 mb-2 flex items-center gap-1.5"><Users className="h-3.5 w-3.5 text-amber-400" /> By staff</div>
                <div className="rounded-xl border divide-y text-sm" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }}>
                  {stats.by_staff.map((s, i) => (
                    <div key={i} className="px-4 py-2.5 flex items-center justify-between" data-testid="show-staff-stat">
                      <span className="text-slate-200 truncate">{s.name}</span>
                      <span className="text-[11px] text-slate-500 flex-1 text-right pr-4">{s.units} sold · {s.trades} trades</span>
                      <span className="font-mono-num text-amber-400 w-24 text-right">{money(s.sales + s.trade_value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="rounded-xl border divide-y text-sm" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }}>
              <div className="px-4 py-2.5 flex justify-between"><span className="text-slate-400">Trade credit given</span><span className="font-mono-num text-slate-200">{money(stats.trades.credit_in)}</span></div>
              <div className="px-4 py-2.5 flex justify-between"><span className="text-slate-400">Cards traded out / in</span><span className="font-mono-num text-slate-200">{stats.trades.cards_out} / {stats.trades.cards_in}</span></div>
              <div className="px-4 py-2.5 flex justify-between"><span className="text-slate-400">Sales at list value</span><span className="font-mono-num text-slate-200">{money(stats.sales.list_value)}</span></div>
            </div>
            <p className="text-[11px] text-slate-600">Stats cover in-person sales (POS/manual) & trades plus purchases made during the show window. Online Shopify orders are excluded.</p>
          </div>
        )}
      </div>
    </div>
  );
}
